import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FocalPointPicker from "../../components/admin/FocalPointPicker";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { apiFetch, ApiError } from "../../lib/api";
import { photoUrl } from "../../lib/assets";
import { ADMIN_DASHBOARD_PATH } from "../../lib/adminPaths";

const SLOT_COUNT = 5;

/** @typedef {{ slot: number, imageKey?: string | null, url?: string | null, caption?: string, focusX?: number, focusY?: number }} LatestPhotoSlot */

/**
 * @param {LatestPhotoSlot[]} photos
 * @returns {LatestPhotoSlot[]}
 */
function normalizeSlots(photos) {
  const bySlot = new Map((photos || []).map((photo) => [Number(photo.slot), photo]));
  return Array.from({ length: SLOT_COUNT }, (_, index) => {
    const slot = index + 1;
    return (
      bySlot.get(slot) || {
        slot,
        imageKey: null,
        url: null,
        caption: "",
        focusX: 50,
        focusY: 50,
      }
    );
  });
}

function LatestPhotoSlotCard({ slot, initial, onUpdate }) {
  const [photo, setPhoto] = useState(initial);
  const [caption, setCaption] = useState(initial.caption || "");
  const [focusX, setFocusX] = useState(initial.focusX ?? 50);
  const [focusY, setFocusY] = useState(initial.focusY ?? 50);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPhoto(initial);
    setCaption(initial.caption || "");
    setFocusX(initial.focusX ?? 50);
    setFocusY(initial.focusY ?? 50);
  }, [initial]);

  const imageSrc = photo.imageKey ? photoUrl(photo) : null;
  const busy = uploading || saving || clearing;

  async function handleUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("photo", file);
      const body = await apiFetch(`/admin/home-latest-photos/${slot}/image`, {
        method: "POST",
        body: formData,
      });
      const saved = body.data;
      setPhoto(saved);
      setCaption(saved.caption || "");
      setFocusX(typeof saved.focusX === "number" ? saved.focusX : 50);
      setFocusY(typeof saved.focusY === "number" ? saved.focusY : 50);
      onUpdate(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed — try again.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const body = await apiFetch(`/admin/home-latest-photos/${slot}`, {
        method: "PATCH",
        body: JSON.stringify({
          caption: caption.trim(),
          focusX,
          focusY,
        }),
      });
      const saved = body.data;
      setPhoto(saved);
      setCaption(saved.caption || "");
      setFocusX(typeof saved.focusX === "number" ? saved.focusX : 50);
      setFocusY(typeof saved.focusY === "number" ? saved.focusY : 50);
      onUpdate(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed — try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!photo.imageKey) return;
    if (!window.confirm(`Clear the image in slot ${slot}?`)) {
      return;
    }

    setClearing(true);
    setError("");

    try {
      const body = await apiFetch(`/admin/home-latest-photos/${slot}/image`, {
        method: "DELETE",
      });
      setPhoto(body.data);
      onUpdate(body.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Clear failed — try again.");
    } finally {
      setClearing(false);
    }
  }

  return (
    <article className="admin-latest-slot">
      <div className="admin-latest-slot__header">
        <p className="admin-eyebrow">Slot {slot}</p>
        {photo.imageKey ? (
          <span className="admin-tag admin-tag--filled">Image set</span>
        ) : (
          <span className="admin-tag">Empty</span>
        )}
      </div>

      {imageSrc ? (
        <FocalPointPicker
          src={imageSrc}
          alt={caption || `Latest photo slot ${slot}`}
          focusX={focusX}
          focusY={focusY}
          onChange={({ focusX: nextX, focusY: nextY }) => {
            setFocusX(nextX);
            setFocusY(nextY);
          }}
        />
      ) : (
        <div className="admin-latest-slot__empty">No image uploaded yet.</div>
      )}

      <div className="admin-latest-slot__actions">
        <label className="admin-button admin-button--ghost admin-story-cover-upload">
          {uploading ? "Uploading…" : photo.imageKey ? "Replace image" : "Upload image"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            disabled={busy}
            onChange={(event) => void handleUpload(event)}
          />
        </label>
        {photo.imageKey ? (
          <button
            className="admin-button admin-button--ghost admin-button--danger"
            type="button"
            disabled={busy}
            onClick={() => void handleClear()}
          >
            {clearing ? "Clearing…" : "Clear image"}
          </button>
        ) : null}
      </div>

      <form className="admin-form admin-latest-slot__form" onSubmit={(event) => void handleSave(event)}>
        <div className="admin-field">
          <label className="admin-label" htmlFor={`latest-caption-${slot}`}>
            Caption <span className="admin-label-optional">(optional)</span>
          </label>
          <input
            className="admin-input"
            id={`latest-caption-${slot}`}
            type="text"
            value={caption}
            placeholder="leopard"
            disabled={busy}
            onChange={(event) => setCaption(event.target.value)}
          />
          <p className="admin-hint">Shown on the home page as [ caption ].</p>
        </div>
        {error ? <p className="admin-error">{error}</p> : null}
        <button className="admin-button" type="submit" disabled={busy}>
          {saving ? "Saving…" : "Save slot"}
        </button>
      </form>
    </article>
  );
}

export default function AdminLatestPhotosPage() {
  const { logout } = useAdminAuth();
  const [slots, setSlots] = useState(/** @type {LatestPhotoSlot[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await apiFetch("/admin/home-latest-photos");
      setSlots(normalizeSlots(body.data.photos || []));
    } catch (err) {
      setSlots(normalizeSlots([]));
      setError(err instanceof ApiError ? err.message : "Couldn't load slots — try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadSlots();
  }, [loadSlots]);

  function handleSlotUpdate(updated) {
    setSlots((current) =>
      current.map((slot) => (slot.slot === updated.slot ? updated : slot))
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <Link className="admin-back-link" to={ADMIN_DASHBOARD_PATH}>
            ← Dashboard
          </Link>
          <p className="admin-eyebrow">HOME PAGE</p>
          <h1 className="admin-title admin-title--compact">Latest Photographs</h1>
        </div>
        <button className="admin-button admin-button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      <section className="admin-card">
        <p className="admin-lede">
          Manage the five photos shown in the <strong>Latest Photographs</strong> section on the home page.
        </p>
        <p className="admin-muted">
          Upload an image for each slot, set an optional caption, and adjust the crop anchor if needed.
        </p>
      </section>

      {error ? (
        <section className="admin-card">
          <p className="admin-error">{error}</p>
        </section>
      ) : null}

      {loading ? (
        <section className="admin-card">
          <p className="admin-muted">Loading slots…</p>
        </section>
      ) : (
        <div className="admin-latest-grid">
          {slots.map((slot) => (
            <LatestPhotoSlotCard
              key={slot.slot}
              slot={slot.slot}
              initial={slot}
              onUpdate={handleSlotUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
